import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Radar, CheckCircle2, ShieldCheck, Plug, ArrowRight, ChevronDown, Loader2 } from "lucide-react";

const STATUS = {
  live: { label: "Live", cls: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500", Icon: CheckCircle2 },
  mock: { label: "Mock", cls: "bg-amber-100 text-amber-700", bar: "bg-amber-400", Icon: ShieldCheck },
  not_connected: { label: "Not connected", cls: "bg-gray-200 text-gray-500", bar: "bg-gray-300", Icon: Plug },
};

const SupplierChips = ({ rows, tone }) => (
  <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
    {rows.length === 0 && <p className="text-xs italic text-ek-ink/35">None</p>}
    {rows.map((r) => (
      <div key={r.supplier_id} className="flex items-center justify-between gap-2 rounded bg-white/70 px-2 py-1 text-xs">
        <span className="truncate text-ek-ink/75">{r.name}</span>
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${tone === "covered" ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
          {r.country || "—"}{r.tier != null ? ` · T${r.tier}` : ""}
        </span>
      </div>
    ))}
  </div>
);

const FeedDetail = ({ provider, name }) => {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/intelligence/coverage/${provider}`).then(({ data }) => setDetail(data)).catch(() => setDetail(false));
  }, [provider]);

  if (detail === null)
    return <div className="flex items-center gap-2 py-3 text-xs text-ek-ink/65"><Loader2 size={13} className="animate-spin" /> Loading coverage…</div>;
  if (!detail) return <p className="py-3 text-xs text-red-500">Couldn&apos;t load coverage detail.</p>;

  return (
    <div className="mt-3 grid grid-cols-1 gap-4 border-t border-black/5 pt-3 sm:grid-cols-2" data-testid={`coverage-detail-${provider}`}>
      <div>
        <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-emerald-700">
          <CheckCircle2 size={12} /> Enriched ({detail.counts.covered})
        </p>
        <SupplierChips rows={detail.covered} tone="covered" />
      </div>
      <div>
        <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-ek-ink/65">
          <Plug size={12} /> Gap — license to unlock ({detail.counts.gap})
        </p>
        <SupplierChips rows={detail.gap} tone="gap" />
        {detail.counts.gap > 0 && (
          <p className="mt-2 text-[11px] text-ek-ink/65">
            Licensing <span className="font-semibold text-ek-ink/70">{name}</span> would enrich {detail.counts.covered} third
            part{detail.counts.covered === 1 ? "y" : "ies"} with live external data.
          </p>
        )}
      </div>
    </div>
  );
};

export const IntelligenceCoverage = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/intelligence/coverage").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (!data || !data.total_suppliers) return null;
  const { summary, feeds, total_suppliers } = data;

  return (
    <section className="rounded-md border border-black/5 bg-white p-6" data-testid="intelligence-coverage">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ek-ink">
            <Radar size={17} className="text-ek-purple" /> Intelligence coverage
          </h2>
          <p className="mt-1 max-w-xl text-sm text-ek-ink/65">
            External risk feeds enriching your {total_suppliers} third parties — click a feed to see exactly
            which it covers and the gap.
          </p>
        </div>
        <div className="text-right" data-testid="coverage-headline">
          <p className="font-display text-3xl font-bold text-ek-purple">{summary.coverage_pct}%</p>
          <p className="text-xs text-ek-ink/65">
            {summary.suppliers_enriched} of {total_suppliers} enriched · {summary.active_feeds}/{summary.total_feeds} feeds active
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {feeds.map((f) => {
          const S = STATUS[f.status] || STATUS.not_connected;
          const pct = f.total ? Math.round((f.covered / f.total) * 100) : 0;
          const isOpen = open === f.provider;
          return (
            <div key={f.provider} className="rounded-md border border-black/5 bg-ek-canvas p-3" data-testid={`coverage-feed-${f.provider}`}>
              <button
                onClick={() => setOpen(isOpen ? null : f.provider)}
                data-testid={`coverage-toggle-${f.provider}`}
                className="w-full text-left"
                aria-expanded={isOpen}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <ChevronDown size={14} className={`shrink-0 text-ek-ink/65 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    <span className="text-sm font-semibold text-ek-ink">{f.name}</span>
                    <span className="ml-1 hidden text-xs text-ek-ink/65 sm:inline">{f.category}</span>
                  </div>
                  <span className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${S.cls}`} data-testid={`coverage-status-${f.provider}`}>
                    <S.Icon size={11} /> {S.label}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/5">
                    <div className={`h-full rounded-full ${S.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-ek-ink/65">{f.covered}/{f.total}</span>
                </div>
              </button>
              {isOpen && <FeedDetail provider={f.provider} name={f.name} />}
            </div>
          );
        })}
      </div>

      <Link
        to="/integrations"
        data-testid="coverage-manage-link"
        className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-ek-purple hover:underline"
      >
        License a feed to switch it from mock to live external data
        <ArrowRight size={13} />
      </Link>
    </section>
  );
};
